/**
 * Seed utilities for validating and decoding game seeds on the frontend
 * 
 * Seed format: UUUU-GEE-MMSS-HHHHH-RRRR (20 chars + 4 dashes = 24 total)
 * - UUUU: Random unique identifier (4 chars)
 * - G: Game mode (1 char)
 * - EE: Expansions bitmask (2 chars)
 * - MM: Map types bitmask (2 chars)
 * - SS: Settings encoded (2 chars for rounds + timer)
 * - HHHHH: Hazard settings (5 chars)
 * - RRRR: Random component for photosphere selection (4 chars)
 */

import { GameMode, GameHazard } from '@/lib/types/common/GameMode';
import { Expansion } from '@/lib/types/common/Expansion';
import { MapType } from '@/lib/types/common/MapType';

// Base32-like alphabet (removed confusing chars: 0, O, 1, I, L)
const SEED_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const BASE = SEED_CHARS.length; // 32

// Game mode mapping (index -> GameMode)
const GAME_MODE_MAP: GameMode[] = [
    GameMode.CLASSIC,
    GameMode.TIMED,
    GameMode.FIGHT,
    GameMode.ENDLESS,
    GameMode.FLASH,
    GameMode.HAZARD,
    GameMode.COOP,
    GameMode.DAILY,
    GameMode.CUSTOM
];

// Expansion order for bitmask
const EXPANSION_ORDER: Expansion[] = [
    Expansion.ARR,
    Expansion.HW,
    Expansion.SB,
    Expansion.ShB,
    Expansion.EW,
    Expansion.DT
];

// Map type order for bitmask
const MAP_TYPE_ORDER: MapType[] = [
    MapType.MAP,
    MapType.DUNGEON,
    MapType.TRIAL,
    MapType.RAID8,
    MapType.RAID24,
    MapType.OTHER,
    MapType.WORLD_MAP,
    MapType.REGION
];

// Hazard order for bitmask
const HAZARD_ORDER: GameHazard[] = [
    GameHazard.PETRIFY,
    GameHazard.SUBTRACTIVE_PALLETTE,
    GameHazard.SKYBLIND,
    GameHazard.BLIND,
    GameHazard.SLEEP,
    GameHazard.REVERSE,
    GameHazard.FORCED_MARCH,
    GameHazard.MINI,
    GameHazard.TOAD,
    GameHazard.HASTE,
    GameHazard.CHARMED,
    GameHazard.PIXELATE,
    GameHazard.MASCOT,
    GameHazard.STATIC
];

// Decoding helpers
function charsToNumber(chars: string): number {
    let result = 0;
    for (let i = 0; i < chars.length; i++) {
        result = result * BASE + SEED_CHARS.indexOf(chars[i]);
    }
    return result;
}

// Bitmask helpers
function bitmaskToExpansions(mask: number): string[] {
    const expansions: string[] = [];
    EXPANSION_ORDER.forEach((exp, index) => {
        if (mask & (1 << index)) {
            expansions.push(exp);
        }
    });
    return expansions;
}

function bitmaskToMapTypes(mask: number): string[] {
    const mapTypes: string[] = [];
    MAP_TYPE_ORDER.forEach((mt, index) => {
        if (mask & (1 << index)) {
            mapTypes.push(mt);
        }
    });
    return mapTypes;
}

function bitmaskToHazards(mask: number): string[] {
    const hazards: string[] = [];
    HAZARD_ORDER.forEach((h, index) => {
        if (mask & (1 << index)) {
            hazards.push(h);
        }
    });
    return hazards;
}

export interface DecodedSeed {
    gameMode: GameMode;
    maxRounds: number;
    timerSeconds: number;
    flashDuration: number;
    hazardsPerRound: number;
    enabledHazards: string[];
    expansions: string[];
    mapTypes: string[];
    randomPart: string;
}

/**
 * Validates a seed format
 * Format: XXXX-XXX-XXXXX-XXXXX-XXXX (21 chars + 4 dashes = 25 total)
 */
export function validateSeedFormat(seed: string): boolean {
    const normalized = normalizeSeed(seed);
    const pattern = /^[A-Z2-9]{4}-[A-Z2-9]{3}-[A-Z2-9]{5}-[A-Z2-9]{5}-[A-Z2-9]{4}$/;
    return pattern.test(normalized);
}

/**
 * Normalizes seed to uppercase format
 */
export function normalizeSeed(seed: string): string {
    return seed.toUpperCase().replace(/[^A-Z2-9-]/g, '');
}

/**
 * Decodes a seed back to game settings
 */
export function decodeSeed(seed: string): DecodedSeed | null {
    try {
        const normalized = normalizeSeed(seed);
        const parts = normalized.split('-');
        
        if (parts.length !== 5) {
            console.error('Invalid seed format: expected 5 parts, got', parts.length);
            return null;
        }
        
        const [uniquePart, part2, part3, hazardPart, randomPart] = parts;
        
        // Validate lengths
        if (uniquePart.length !== 4 || part2.length !== 3 || part3.length !== 5 || 
            hazardPart.length !== 5 || randomPart.length !== 4) {
            console.error('Invalid seed format: incorrect part lengths');
            return null;
        }
        
        // Decode game mode (1 char)
        const gameModeIndex = charsToNumber(part2[0]);
        const gameMode = GAME_MODE_MAP[gameModeIndex] || GameMode.TIMED;
        
        // Decode expansions (2 chars)
        const expansionMask = charsToNumber(part2.slice(1, 3));
        const expansions = bitmaskToExpansions(expansionMask);
        
        // Decode map types (2 chars)
        const mapTypeMask = charsToNumber(part3.slice(0, 2));
        const mapTypes = bitmaskToMapTypes(mapTypeMask);
        
        // Decode settings (3 chars)
        const settingsValue = charsToNumber(part3.slice(2, 5));
        const rounds = (settingsValue >> 9) & 0x1F;
        const timerSeconds = settingsValue & 0x1FF; // 9 bits for timer (0-511)
        const maxRounds = rounds === 0 ? -1 : rounds;
        
        // Decode hazard settings (5 chars)
        const hazardValue = charsToNumber(hazardPart);
        const hazardMask = (hazardValue >> 10) & 0x1FFF; // 13 bits
        const hazardsPerRound = (hazardValue >> 6) & 0xF; // 4 bits
        const flashValue = hazardValue & 0x3F; // 6 bits
        const enabledHazards = bitmaskToHazards(hazardMask);
        const flashDuration = flashValue * 100 || 1000;
        
        return {
            gameMode,
            maxRounds,
            timerSeconds,
            flashDuration,
            hazardsPerRound: hazardsPerRound || 1,
            enabledHazards: enabledHazards.length > 0 ? enabledHazards : Object.values(GameHazard),
            expansions: expansions.length > 0 ? expansions : [Expansion.ARR],
            mapTypes: mapTypes.length > 0 ? mapTypes : [MapType.MAP],
            randomPart
        };
    } catch (error) {
        console.error('Error decoding seed:', error);
        return null;
    }
}

/**
 * Formats a seed input with dashes
 * New format: XXXX-XXX-XXXXX-XXXXX-XXXX (21 chars + 4 dashes)
 */
export function formatSeedInput(value: string): string {
    // Remove any non-alphanumeric characters except dashes
    let cleaned = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    
    // Remove all existing dashes for reformatting
    cleaned = cleaned.replace(/-/g, '');
    
    // Limit to 21 characters (4 + 3 + 5 + 5 + 4)
    cleaned = cleaned.substring(0, 21);
    
    // Add dashes at positions 4, 7, 12, 17
    let formatted = '';
    for (let i = 0; i < cleaned.length; i++) {
        if (i === 4 || i === 7 || i === 12 || i === 17) {
            formatted += '-';
        }
        formatted += cleaned[i];
    }
    
    return formatted;
}
